'use client';

import type { ReactNode } from 'react';
import { BlogCategoryDocument } from '../../../../../prismicio-types';
import { FilterSidebar } from './FilterSidebar';
import { SearchAndSort } from './SearchAndSort';
import { PostData } from './types';
import { useResourceLibrary } from './useResourceLibrary';

interface ResourceLibraryShellProps {
  categories: BlogCategoryDocument[];
  initialPosts: PostData[];
  initialHasMore: boolean;
  /** Pre-selected category UID from a deep-link (see useResourceLibrary). */
  initialCategory?: string;
  /** Pre-filled search term from a deep-link. */
  initialSearch?: string;
  searchPlaceholder: string;
  loadMoreLabel: string;
  /** Classes applied to the results grid — controls columns/gaps per variant. */
  gridClassName: string;
  /** Renders a single card. Variants supply the layout they want. */
  renderCard: (post: PostData) => ReactNode;
  /** Outer <section> classes — variants override for tighter/looser bounds. */
  sectionClassName?: string;
  /** Inner container classes — controls the sidebar+content flex row width. */
  containerClassName?: string;
  /** Extra classes applied to the sidebar wrapper (e.g. negative margins). */
  sidebarClassName?: string;
  /** Filter panel chrome (border, radius, background) — forwarded to sidebar. */
  sidebarPanelClassName?: string;
  /** Type-section option labels — forwarded to sidebar. */
  filterTypeLabelClassName?: string;
  /** Topic-section option labels — forwarded to sidebar. */
  filterTopicLabelClassName?: string;
  /** Search input typography — forwarded to SearchAndSort. */
  searchInputClassName?: string;
  /** Sort option typography — forwarded to SearchAndSort. */
  sortLabelClassName?: string;
  /** Wrapper around the Load More button (alignment, spacing). */
  loadMoreWrapperClassName?: string;
  /** Load More button classes (replaces the default pill styling). */
  loadMoreButtonClassName?: string;
}

const DEFAULT_SECTION_CLASS =
  'bg-[#f9f9f9] px-5 py-16 md:px-10 lg:px-16 xl:px-[120px] 2xl:px-[150px]';
const DEFAULT_CONTAINER_CLASS =
  'mx-auto flex max-w-[1440px] flex-col gap-6 lg:flex-row lg:gap-6';
const DEFAULT_LOAD_MORE_WRAPPER_CLASS = 'flex justify-center pt-6';
const DEFAULT_LOAD_MORE_BUTTON_CLASS =
  'rounded-full border border-[#041118] px-6 py-3 text-base font-bold text-[#041118] transition-colors hover:bg-[#041118]/5';

/**
 * The chrome shared by every ResourceLibraryGrid variant: filter sidebar,
 * search + sort, results grid, load more. Variants pass their own card
 * renderer and grid layout classes; behavior is identical across variants.
 */
export function ResourceLibraryShell({
  categories,
  initialPosts,
  initialHasMore,
  initialCategory,
  initialSearch,
  searchPlaceholder,
  loadMoreLabel,
  gridClassName,
  renderCard,
  sectionClassName = DEFAULT_SECTION_CLASS,
  containerClassName = DEFAULT_CONTAINER_CLASS,
  sidebarClassName,
  sidebarPanelClassName,
  filterTypeLabelClassName,
  filterTopicLabelClassName,
  searchInputClassName,
  sortLabelClassName,
  loadMoreWrapperClassName = DEFAULT_LOAD_MORE_WRAPPER_CLASS,
  loadMoreButtonClassName = DEFAULT_LOAD_MORE_BUTTON_CLASS,
}: ResourceLibraryShellProps) {
  const {
    posts,
    hasMore,
    loading,
    initialLoading,
    activeTypes,
    activeCategories,
    toggleType,
    toggleCategory,
    clearAll,
    searchQuery,
    setSearchQuery,
    isSearchActive,
    setSortBy,
    effectiveSortBy,
    loadMore,
  } = useResourceLibrary({
    initialPosts,
    initialHasMore,
    initialCategory,
    initialSearch,
  });

  return (
    <section className={sectionClassName}>
      <div className={containerClassName}>
        {/* Sidebar */}
        <FilterSidebar
          categories={categories}
          activeTypes={activeTypes}
          activeCategories={activeCategories}
          onToggleType={toggleType}
          onToggleCategory={toggleCategory}
          onClearAll={clearAll}
          className={sidebarClassName}
          panelClassName={sidebarPanelClassName}
          typeLabelClassName={filterTypeLabelClassName}
          topicLabelClassName={filterTopicLabelClassName}
        />

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <SearchAndSort
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={searchPlaceholder}
            isSearchActive={isSearchActive}
            effectiveSortBy={effectiveSortBy}
            onSortChange={setSortBy}
            searchInputClassName={searchInputClassName}
            sortLabelClassName={sortLabelClassName}
          />

          {/* Card grid */}
          {initialLoading ? (
            <div className={gridClassName}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[455px] animate-pulse rounded-2xl bg-gray-200"
                />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-lg font-semibold text-gray-600">
                No results found
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your filters or search terms
              </p>
            </div>
          ) : (
            <div className={gridClassName}>{posts.map(renderCard)}</div>
          )}

          {/* Loading spinner for load more */}
          {loading && !initialLoading && (
            <div className="flex justify-center py-6">
              <div className="size-8 animate-spin rounded-full border-2 border-[#4b0fea] border-t-transparent" />
            </div>
          )}

          {/* Load more */}
          {hasMore && !loading && (
            <div className={loadMoreWrapperClassName}>
              <button
                type="button"
                onClick={loadMore}
                className={loadMoreButtonClassName}
              >
                {/* Wrapped in a span so variants using the OutlineCtaLink
                    class list (which relies on `group-hover:underline` on
                    an inner element) get the same hover underline. */}
                <span className="group-hover:underline">{loadMoreLabel}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
